// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { PlanMultiMoveIntersection } from '@/asset/api/multi-move';
import { dateTimeSchema } from '@/utilities/date-time-schema';
import { FieldError } from 'react-hook-form';
import { z } from 'zod';

const assetSchema = z.object({
  id: z.string(),
  key: z.string(),
  serial: z.string(),
});

const locationSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
});

const formItemSchema = z
  .object({
    id: z.string(),
    asset: assetSchema.nullable(),
    origin: locationSchema.nullable(),
    destination: locationSchema.nullable(),
    shipDate: dateTimeSchema.nullable(),
    deliveryDate: dateTimeSchema.nullable(),
  })
  .superRefine((move, context) => {
    if (!move.asset) {
      context.addIssue({
        code: 'custom',
        path: ['asset'],
        message: 'Asset is required',
      });
    }
    if (move.origin === null) {
      context.addIssue({
        code: 'custom',
        path: ['origin'],
        message: 'Origin is required',
      });
    }
    if (move.destination === null) {
      context.addIssue({
        code: 'custom',
        path: ['destination'],
        message: 'Destination is required',
      });
    }
    if (!move.shipDate && !move.deliveryDate) {
      context.addIssue({
        code: 'custom',
        path: ['shipDate'],
        message: 'Ship or delivery date is required',
      });
      context.addIssue({
        code: 'custom',
        path: ['deliveryDate'],
        message: 'Delivery or ship date is required',
      });
    }
    if (
      move.shipDate &&
      move.deliveryDate &&
      move.shipDate > move.deliveryDate
    ) {
      context.addIssue({
        code: 'custom',
        path: ['shipDate'],
        message: 'Ship date cannot be after the delivery date',
      });
    }
  });

export const multiMoveFormSchema = z.object({
  moves: z.array(formItemSchema),
});
export type MultiMoveFormData = z.infer<typeof multiMoveFormSchema>;
export type MultiMoveFormItemData = z.infer<typeof formItemSchema>;

export const multiMoveFormKeys = [
  { id: 'asset' as MultiMoveFormKey, label: 'Asset', required: true },
  { id: 'origin' as MultiMoveFormKey, label: 'Origin', required: true },
  {
    id: 'destination' as MultiMoveFormKey,
    label: 'Destination',
    required: false,
  },
  { id: 'shipDate' as MultiMoveFormKey, label: 'Ship Date', required: true },
  {
    id: 'deliveryDate' as MultiMoveFormKey,
    label: 'Delivery Date',
    required: false,
  },
];

export type MultiMoveFormKey =
  | 'asset'
  | 'origin'
  | 'destination'
  | 'shipDate'
  | 'deliveryDate';
export type MultiMoveRowStatus =
  | 'valid'
  | 'invalid'
  | 'uninitialized'
  | 'pending'
  | 'unknown';
const formKeys = [
  'asset',
  'origin',
  'destination',
  'shipDate',
  'deliveryDate',
] as const;

export interface PlannedMove extends MultiMoveFormItemData {
  status: MultiMoveRowStatus;
  isValidating: boolean;
  altered: {
    asset: boolean;
    origin: boolean;
    destination: boolean;
    shipDate: boolean;
    deliveryDate: boolean;
  };
  lastIntersectionError?: PlanMultiMoveIntersection;
  runAfter?: number;
}

/**
 * Determines the validation status of a move based on its ID from the provided list of planned moves.
 *
 * @param {PlannedMove[]} moves - An array of planned move objects to be searched.
 * @param {string} id - The identifier of the move whose status is to be retrieved.
 * @return {MultiMoveRowStatus} The validation status of the move. Returns 'unknown' if no match is found.
 */
export function getPlannedMoveValidationStatus(
  moves: PlannedMove[],
  id: string
): MultiMoveRowStatus {
  const status = moves.find((item) => item.id === id);
  if (status) {
    return status.status;
  }
  return 'unknown';
}

/**
 * Retrieves the details of a specific row based on the provided ID.
 *
 * @param {PlannedMove[]} moves - An array of PlannedMove objects to search through.
 * @param {string} id - The ID of the row to find within the moves array.
 * @return {PlannedMove | undefined} The details of the row matching the provided ID, or undefined if no match is found.
 */
export function getPlannedMoveDetails(
  moves: PlannedMove[],
  id: string
): PlannedMove | undefined {
  const row = moves.find((item) => item.id === id);
  if (row) {
    return row;
  }
  return undefined;
}

type FieldErrorArray = Record<MultiMoveFormKey, FieldError>;

/**
 * Checks if the provided error object contains custom error types.
 *
 * @param {FieldError | FieldErrorArray | undefined} error - The error object to inspect. Can be undefined, a single error, or an array of errors.
 * @return {boolean} Returns true if any error in the provided object has a type of 'custom', otherwise false.
 */
export function errorHasCustomErrors(
  error: FieldError | FieldErrorArray | undefined
): boolean {
  if (!error) {
    return false;
  }

  if (formKeys.some((key) => key in error)) {
    return (Object.values(error) as FieldError[]).some((item) => {
      return item.type === 'custom';
    });
  }

  return (error as FieldError).type === 'custom';
}
